import { Button } from '@mantine/core';
import {
  useRef, useState, useEffect, useCallback,
} from 'react';
import vars from '@/config/vars';
import { useDispatch, useSelector } from 'react-redux';
import useInactivity from '@/hooks/useInactivity';
import { useDisclosure } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import { notifications } from '@mantine/notifications';
import { getMutationResult } from '@/helpers/query';
import { useUserLogoutMutation, usePingAuthMutation } from '@/services/api';
import { resetUser } from '@/data/features/user/user';
import { IconHourglass } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import routes from '@/routes';
import Modal from '../Modal';

const Inactivity = () => {
  const { t } = useTranslation();
  const [opened, { open, close }] = useDisclosure(false);
  const { refreshTokenId, refreshToken } = useSelector((state) => state.user);
  const { popupCountdownSeconds, showSeconds } = vars.inactivity;
  const [apiUserLogout] = useUserLogoutMutation();
  const countdownRef = useRef(null);
  const startTimeRef = useRef(null);
  const dispatch = useDispatch();
  const [count, setCount] = useState(popupCountdownSeconds);
  const [pingAuth] = usePingAuthMutation();
  const logoutCalledRef = useRef(false);

  const isInactive = useInactivity(showSeconds);
  const navigate = useNavigate();

  const handleInactivityLogout = useCallback(async () => {
    if (logoutCalledRef.current) return;
    logoutCalledRef.current = true;
    try {
      const logoutData = { refreshTokenId, refreshToken, forceLogout: false };
      const { isError } = await getMutationResult(logoutData, apiUserLogout);
      if (!isError) {
        dispatch({ type: 'RESET_ALL' });
        dispatch(resetUser());
        navigate(routes.login.path);
      } else {
        setCount(popupCountdownSeconds);
        close();
        dispatch({ type: 'RESET_ALL' });
        navigate('/');
      }
    } catch (error) {
      notifications.show({
        title: t('globals.error.title'),
        message: t('globals.error.message'),
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buttonText = (count > 0) ? t('components.inactivity.button.continue', { count }) : '';
  const modalText = (count > 0) ? t('components.inactivity.modal.body.continue') : '';

  if (!opened && isInactive) {
    if (countdownRef.current) clearInterval(countdownRef.current);
    startTimeRef.current = Date.now();

    countdownRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
      const newValue = popupCountdownSeconds - elapsed;
      setCount(() => {
        if (newValue <= 0) {
          if (countdownRef.current) clearInterval(countdownRef.current);

          handleInactivityLogout();
        }

        return (newValue <= 0) ? 0 : newValue;
      });
    }, 1000);

    open();
  }

  /**
   * count 0 means the user has been kicked out from application
   * so we need to move him back to homepage
   */
  const handleCloseInActivity = async () => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }

    if (count <= 0) {
      handleInactivityLogout();
    } else {
      try {
        await getMutationResult({ refreshTokenId, refreshToken }, pingAuth);
      } catch (error) {
        //
      }
    }
    close();
    setCount(popupCountdownSeconds);
    logoutCalledRef.current = false;
  };

  // Track window focus to handle inactivity when returning to the app
  useEffect(() => {
    const handleFocus = () => {
      if (isInactive && count <= 0) {
        handleInactivityLogout();
        setCount(popupCountdownSeconds);
      }
    };
    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
      logoutCalledRef.current = false;
    };
  }, [isInactive, count, handleInactivityLogout, popupCountdownSeconds]);

  const actions = <Button test="active" onClick={handleCloseInActivity}>{buttonText}</Button>;

  return (
    <>
      <Modal
        opened={opened}
        title={t('components.inactivity.modal.title')}
        showClose={false}
        footer={actions}
        size='lg'
        avatar={<IconHourglass stroke={1} width='112' height='112' />}
      >
        {modalText}
      </Modal>
    </>
  );
};

export default Inactivity;
